import { getUserFacingDatasetName } from './datasetCloneNames';
import {
    deriveDatasetStepStates,
    formatStagePct,
    generationStepLabel,
} from './datasetProgressSteps';
import { generationStepSubtitle } from './generationPhaseLabels';
import { deriveRetrievalAgentSteps } from './retrievalAgentProgress';

/**
 * Rows for the "Uploading / Processing your data…" placeholders of the Data
 * Editor and Review Training Plan: one line per running data flow of a task,
 * naming the stage it is in, e.g.
 *
 *   rows.csv · Uploading · 42%
 *   rows.csv · Analyzing file · 2,726,410 rows read
 *   rows.csv · Importing rows · 12%
 *   rows.csv · Generating labels · 30%
 *   task_ai · Generating examples · 120 / 1,000 processed
 *   Retrieval Agent · Searching public datasets
 *
 * Shape: `{ name, stage, detail, percent, isBenchmark, processing }` where
 * `processing` is true once the file is on the server (anything past the
 * signed PUT), i.e. records may start to appear while the row is shown.
 */

/** Stage + detail of an upload card (file upload, library clone, Hugging Face import). */
export function uploadStageSummary(entry) {
    const states = deriveDatasetStepStates(entry || {});
    if (states.labelGenState === 'active') {
        return { stage: generationStepLabel(entry), detail: formatStagePct(states.labelGenPct) };
    }
    if (states.processingState === 'active') {
        return {
            stage: 'Importing rows',
            detail: states.processingPctLabel || formatStagePct(states.processingPctDisplay),
        };
    }
    if (states.analyzeState === 'active') {
        return { stage: 'Analyzing file', detail: states.analyzeSubtitle || states.analyzeRight };
    }
    if (states.uploadState === 'active') {
        return { stage: 'Uploading', detail: `${states.uploadPctDisplay}%` };
    }
    // Just flipped phase, or waiting for the data processor to pick the file up.
    return { stage: entry?.phase === 'uploading' ? 'Uploading' : 'Processing', detail: null };
}

/** An active Generate-with-AI card. */
function generationRow(entry) {
    return {
        stage: entry.isTrajectoryGeneration ? 'Generating trajectories' : 'Generating examples',
        detail: generationStepSubtitle({
            jobPhase: entry.jobPhase,
            processedPrompts: entry.processedPrompts,
            totalPrompts: entry.totalPrompts,
        }),
    };
}

/** An active Retrieval Agent session: the label of its running step. */
function retrievalRow(entry) {
    const active = deriveRetrievalAgentSteps(entry).find((step) => step.state === 'active');
    return { stage: 'Retrieval Agent', detail: active?.label || null };
}

/**
 * Placeholder row for one entry of `listActiveDataFlowsForUsecase`
 * (`flowType` upload | generation | retrieval) or of
 * `listActiveUploadEntriesForUsecase` (no `flowType` → upload).
 */
export function inFlightRowFromEntry(entry) {
    const flowType = entry?.flowType || 'upload';
    const summary =
        flowType === 'generation'
            ? generationRow(entry)
            : flowType === 'retrieval'
              ? retrievalRow(entry)
              : uploadStageSummary(entry);
    return {
        name: getUserFacingDatasetName(entry?.datasetName || entry?.progressKey || ''),
        percent: Math.round(Number(entry?.uploadProgress) || 0),
        isBenchmark: Boolean(entry?.isBenchmark),
        processing: flowType !== 'upload' || entry?.phase !== 'uploading',
        ...summary,
    };
}

/** `rows.csv · Analyzing file · 2,726,410 rows read` (legacy `rows.csv · 42%` without a stage). */
export function formatInFlightRow({ name, stage, detail, percent }) {
    if (!stage) return `${name} · ${Math.round(Number(percent) || 0)}%`;
    return [name, stage, detail].filter(Boolean).join(' · ');
}
